import React from 'react';
import { MapPin, Clock, Phone, Mail } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { IMG } from '../lib/images';
import { SEO } from '../components/SEO';

export default function BoutiquePage() {
  const { t } = useI18n();
  return (
    <main className="bg-background">
      <SEO
        title={t('boutique.title')}
        description="Découvrez la boutique Tahitian Move à Papeete, Polynésie française. Horaires, adresse, contact."
      />

      {/* Hero header */}
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-24 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="eyebrow">Papeete · Polynésie française</p>
          <h1 className="display text-6xl md:text-8xl lg:text-9xl mt-6 max-w-4xl">
            {t('boutique.subtitle')}
          </h1>
        </div>
      </section>

      {/* Boutique main */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7 media-card aspect-[4/3]">
            <img src={IMG.boutique} alt="Tahitian Move Boutique Papeete" className="w-full h-full object-cover" />
          </div>

          <aside className="lg:col-span-5 space-y-10">
            <InfoBlock icon={MapPin} label={t('boutique.addressLabel')}>
              {t('boutique.addressLines').map((l, i) => (
                <p key={i}>{l}</p>
              ))}
            </InfoBlock>
            <InfoBlock icon={Clock} label={t('boutique.hoursLabel')}>
              {t('boutique.hoursLines').map((l, i) => (
                <p key={i}>{l}</p>
              ))}
            </InfoBlock>
            <InfoBlock icon={Phone} label={t('boutique.contactLabel')}>
              <p>{t('boutique.phone')}</p>
              <a href={`mailto:${t('boutique.email')}`} className="link-underline">{t('boutique.email')}</a>
            </InfoBlock>
            <a
              href="https://www.google.com/maps/search/Tahitian+Move+Fare+Ute+Papeete"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              {t('boutique.directionsCta')}
            </a>
          </aside>
        </div>
      </section>

      {/* Note */}
      <section className="py-16 md:py-24 bg-paper border-t border-sand">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="eyebrow">{t('boutique.noteLabel')}</p>
          <p className="font-serif italic text-2xl md:text-3xl text-ink mt-4 leading-snug">
            « {t('boutique.note')} »
          </p>
        </div>
      </section>

      {/* Custom location illustration */}
      <section className="bg-paper py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <p className="eyebrow">Nous trouver</p>
            <h2 className="display text-4xl md:text-5xl mt-4 text-ink">
              Fare Ute, Papeete.
            </h2>
            <p className="text-ink-soft mt-4 max-w-xl mx-auto">
              Nous sommes situés sur Fare Ute, à côté de l’assurance POEMA.
            </p>
          </div>
          <div className="media-card max-w-5xl mx-auto bg-pure border border-sand">
            <img
              src={IMG.locationMap}
              alt="Plan d'accès à la boutique Tahitian Move — Fare Ute, Papeete"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>
    </main>
  );
}

function InfoBlock({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 text-lagoon">
        <Icon size={18} />
        <span className="eyebrow text-ink">{label}</span>
      </div>
      <div className="mt-4 text-ink-soft text-base font-light leading-relaxed space-y-1">{children}</div>
    </div>
  );
}
